import { DateTime } from "luxon";

import type { DateSelectorChangeEvent } from "./DateSelector";

export type GroupedRangePreset =
  | "last30Days"
  | "currentMonth"
  | "lastMonth"
  | "quarter1"
  | "quarter2"
  | "quarter3"
  | "quarter4"
  | "currentYear"
  | "lastYear";

const getCurrentYear = () => DateTime.now().year;
const getCurrentQuarter = () => DateTime.now().quarter;

const getQuarters = () => {
  const currentQuarter = getCurrentQuarter();
  const currentYear = getCurrentYear();
  const quarterList: {
    rangePreset: GroupedRangePreset;
    rangePresetLabel: string;
    startDate: DateTime;
    endDate: DateTime;
  }[] = [];
  const quarterSubLabels: { [key: number]: string } = {
    1: "(Jan-Mar)",
    2: "(Apr-June)",
    3: "(Jul-Sept)",
    4: "(Oct-Dec)",
  };
  quarterList.push({
    rangePreset: `quarter${currentQuarter!}` as GroupedRangePreset,
    rangePresetLabel: `Q${currentQuarter} ${currentYear} ${
      quarterSubLabels[currentQuarter!]
    }`,
    ...getStartAndEndDatesFromRangePreset(
      `quarter${currentQuarter}` as GroupedRangePreset
    ),
  });
  const orderedQuarters = [4, 3, 2, 1];
  const indexOfTarget = 4 - currentQuarter;
  const numsToRearrange = orderedQuarters.splice(0, indexOfTarget);
  orderedQuarters.push(...numsToRearrange);

  for (let i = 0; i < orderedQuarters.length; i++) {
    const q = orderedQuarters[i];
    if (q !== currentQuarter) {
      quarterList.push({
        rangePreset: `quarter${q}` as GroupedRangePreset,
        rangePresetLabel: `Q${q} ${
          q > currentQuarter! ? currentYear - 1 : currentYear
        } ${quarterSubLabels[q!]!}`,
        ...getStartAndEndDatesFromRangePreset(
          `quarter${q}` as GroupedRangePreset
        ),
      });
    }
  }

  return quarterList;
};

const getStartAndEndDateFromQuarter = (
  q: 1 | 2 | 3 | 4,
  startMonth: number,
  endMonth: number
) => {
  const currentQuarter = getCurrentQuarter();
  const currentYear = getCurrentYear();
  const yearToUse = currentQuarter! >= q ? currentYear : currentYear - 1;
  return {
    startDate: DateTime.local(yearToUse, startMonth, 1).startOf("day"),
    endDate: DateTime.local(yearToUse, endMonth, 1).startOf("day"),
  };
};

export const getStartAndEndDatesFromRangePreset = (
  preset: GroupedRangePreset
) => {
  const endDate = DateTime.now();

  const presetToDateRange = {
    last30Days: {
      startDate: endDate.minus({ days: 30 }).startOf("day"),
      endDate,
    },
    currentMonth: {
      startDate: endDate.startOf("month"),
      endDate,
    },
    lastMonth: {
      startDate: endDate.minus({ month: 1 }).startOf("month"),
      endDate: endDate.minus({ month: 1 }).endOf("month").endOf("day"),
    },
    currentYear: {
      startDate: endDate.startOf("year"),
      endDate,
    },
    lastYear: {
      startDate: endDate.minus({ year: 1 }).startOf("year"),
      endDate: endDate.minus({ year: 1 }).endOf("year"),
    },
    quarter1: getStartAndEndDateFromQuarter(1, 1, 3),
    quarter2: getStartAndEndDateFromQuarter(2, 4, 6),
    quarter3: getStartAndEndDateFromQuarter(3, 7, 9),
    quarter4: getStartAndEndDateFromQuarter(4, 10, 12),
  };

  return presetToDateRange[preset];
};

type Groups = "month" | "year" | "quarter";

export const GROUPED_PRESETS: {
  [key in Groups]: DateSelectorChangeEvent[];
} = {
  month: [
    {
      rangePreset: "last30Days",
      rangePresetLabel: "Last 30 days",
      ...getStartAndEndDatesFromRangePreset("last30Days"),
    },
    {
      rangePreset: "currentMonth",
      rangePresetLabel: "This month (to-date)",
      ...getStartAndEndDatesFromRangePreset("currentMonth"),
    },
    {
      rangePreset: "lastMonth",
      rangePresetLabel: "Last month",
      ...getStartAndEndDatesFromRangePreset("lastMonth"),
    },
  ],
  quarter: getQuarters(),
  year: [
    {
      rangePreset: "currentYear",
      rangePresetLabel: `${getCurrentYear()} (to-date)`,
      ...getStartAndEndDatesFromRangePreset("currentYear"),
    },
    {
      rangePreset: "lastYear",
      rangePresetLabel: (getCurrentYear() - 1).toString(),
      ...getStartAndEndDatesFromRangePreset("lastYear"),
    },
  ],
};
